import { RefObject, useCallback, useEffect, useRef } from 'react';
import pasteParser, { convertPastedActualToCellInput, parseSingleCellClipboardValue } from 'components/FeaturedSpreadsheet/utilities/pasteParser';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import ScalarDatasheetProps, { Selection } from './ScalarDatasheet.props';

interface UseDatasheetClipboardPasteParams {
  data: ICell[][];
  onCellsChanged: ScalarDatasheetProps['onCellsChanged'];
  /** The rectangle the datasheet pastes into. */
  selected: Selection | null;
  containerRef: RefObject<HTMLDivElement | null>;
  enableCopyFeedback?: boolean;
}

const convertPastedActualsForCells = (
  parsedClipboard: any[][],
  data: ICell[][],
  startI: number,
  startJ: number
) =>
  parsedClipboard.map((row, rowOffset) =>
    (row ?? []).map((value: unknown, colOffset: number) =>
      convertPastedActualToCellInput(data?.[startI + rowOffset]?.[startJ + colOffset], value)
    )
  );

// Parses a paste for the given rectangle: a multi-cell rectangle gets the clipboard tiled across it,
// while a single cell takes the clipboard whole from that cell.
const parseForSelection = (clipboard: string, selection: Selection) => {
  const startI = Math.min(selection.start.i, selection.end.i);
  const startJ = Math.min(selection.start.j, selection.end.j);
  const maxAllowedRows = Math.abs(selection.end.i - selection.start.i) + 1;
  const maxAllowedColumns = Math.abs(selection.end.j - selection.start.j) + 1;
  const bounds = maxAllowedRows * maxAllowedColumns > 1 ? { maxAllowedRows, maxAllowedColumns } : null;
  return { parsed: pasteParser(clipboard, bounds) as any[][], startI, startJ };
};

const useDatasheetClipboardPaste = ({
  data,
  onCellsChanged,
  selected,
  containerRef,
  enableCopyFeedback = false,
}: UseDatasheetClipboardPasteParams) => {
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedRef = useRef<Selection | null>(selected);
  const dataRef = useRef(data);
  const onCellsChangedRef = useRef(onCellsChanged);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    onCellsChangedRef.current = onCellsChanged;
  }, [onCellsChanged]);

  // Flash selected cells after copy so readonly spreadsheet previews confirm clipboard updates.
  useEffect(() => {
    if (!enableCopyFeedback) {
      return undefined;
    }

    const handleCopy = () => {
      const containerEl = containerRef.current;
      if (!containerEl || !selectedRef.current) {
        return;
      }

      const selectedCells = containerEl.querySelectorAll<HTMLElement>('td.selected');
      if (!selectedCells.length) {
        return;
      }

      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
        copyFeedbackTimeoutRef.current = null;
      }
      const ownerWindow = containerEl.ownerDocument.defaultView ?? window;
      selectedCells.forEach(cell => {
        cell.classList.remove('cell-copy-feedback');
      });
      ownerWindow.requestAnimationFrame(() => {
        selectedCells.forEach(cell => cell.classList.add('cell-copy-feedback'));
      });

      copyFeedbackTimeoutRef.current = setTimeout(() => {
        selectedCells.forEach(cell => cell.classList.remove('cell-copy-feedback'));
      }, 950);
    };

    document.addEventListener('copy', handleCopy, true);
    return () => {
      document.removeEventListener('copy', handleCopy, true);
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
        copyFeedbackTimeoutRef.current = null;
      }
    };
  }, [containerRef, enableCopyFeedback]);

  const getClipboardText = (e: ClipboardEvent): string => {
    const direct = e.clipboardData?.getData('text/plain') ?? '';
    if (direct) {
      return direct;
    }

    // Legacy clipboard fallback for environments that still expose clipboardData.
    const legacyClipboardData = (globalThis as any).clipboardData;
    return legacyClipboardData ? legacyClipboardData.getData('Text') : '';
  };

  const applyPasteToSelection = useCallback((parsedClipboard: any[][], startI: number, startJ: number) => {
    const currentOnCellsChanged = onCellsChangedRef.current;
    if (!currentOnCellsChanged) {
      return;
    }

    // Build the exact change/addition payload shape expected by react-datasheet.
    const changes: any[] = [];
    const additions: any[] = [];
    for (let i = 0; i < parsedClipboard.length; i += 1) {
      const row = parsedClipboard[i] ?? [];
      for (let j = 0; j < row.length; j += 1) {
        const rowIndex = startI + i;
        const colIndex = startJ + j;
        const cell = dataRef.current?.[rowIndex]?.[colIndex];
        const value = convertPastedActualToCellInput(cell, row[j]);

        if (!cell) {
          additions.push({ row: rowIndex, col: colIndex, value });
        } else if (!cell.readOnly) {
          changes.push({ cell, row: rowIndex, col: colIndex, value });
        }
      }
    }

    if (additions.length) {
      currentOnCellsChanged(changes as any, additions as any);
    } else {
      currentOnCellsChanged(changes as any);
    }
  }, []);

  // Capture paste before the editor: ranges belong to the grid, single values to its draft.
  useEffect(() => {
    const handlePasteWhileEditing = (e: ClipboardEvent) => {
      const containerEl = containerRef.current;
      if (!containerEl) {
        return;
      }

      const activeEl = document.activeElement as HTMLElement | null;
      if (!activeEl || !containerEl.contains(activeEl)) {
        return;
      }
      if (!['INPUT', 'TEXTAREA'].includes(activeEl.tagName)) {
        return;
      }

      const currentSelected = selectedRef.current;
      if (!currentSelected) {
        return;
      }

      const clipboardText = getClipboardText(e);
      if (!clipboardText) {
        return;
      }

      // The datasheet does not fan out a paste while an editor input is focused, so it is applied here.
      const { parsed, startI, startJ } = parseForSelection(clipboardText, currentSelected);
      const singleCellSelected =
        currentSelected.start.i === currentSelected.end.i && currentSelected.start.j === currentSelected.end.j;
      if (
        singleCellSelected &&
        parseSingleCellClipboardValue(clipboardText) !== null &&
        activeEl.classList.contains('data-editor')
      ) {
        // Single values remain editor drafts; ranges still use the grid's selection path.
        return;
      }
      applyPasteToSelection(parsed, startI, startJ);

      // The grid paste above is authoritative. Prevent the browser from also
      // inserting the clipboard text into the focused editor input.
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('paste', handlePasteWhileEditing, true);
    return () => document.removeEventListener('paste', handlePasteWhileEditing, true);
  }, [applyPasteToSelection, containerRef]);

  // Sized from the rectangle the datasheet is pasting into, read at paste time.
  const handleOnPaste = useCallback((clipboard: string) => {
    const currentSelected = selectedRef.current;
    if (!currentSelected) {
      return pasteParser(clipboard) as string[][];
    }
    const { parsed, startI, startJ } = parseForSelection(clipboard, currentSelected);
    return convertPastedActualsForCells(parsed, dataRef.current, startI, startJ) as string[][];
  }, []);

  return { handleOnPaste };
};

export default useDatasheetClipboardPaste;
